import algoliasearch from 'algoliasearch/lite';
import { useMemo } from 'react';
import { useInstantSearchContext } from 'react-instantsearch';

import type {
  SearchFacetDefinitionInput,
  SearchFilterInput,
  SearchRefinementInput,
} from '#src/__generated__/globalTypes.ts';
import {
  SearchFaceting,
  SearchOperator,
} from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { getAlgoliaHosts } from '#src/lib/algolia.ts';

export const useSearchClient = () => {
  const { algoliaApplicationId, algoliaSearchApiKey } = useConfigContext();

  const searchClient = useMemo(() => {
    return algoliasearch(algoliaApplicationId, algoliaSearchApiKey, {
      hosts: getAlgoliaHosts(algoliaApplicationId),
    });
  }, [algoliaApplicationId, algoliaSearchApiKey]);

  return searchClient;
};

const OPERATORS: Record<string, SearchOperator> = {
  '=': SearchOperator.EQUAL,
  '>': SearchOperator.GREATER_THAN,
  '>=': SearchOperator.GREATER_THAN_OR_EQUAL,
  '<': SearchOperator.LESS_THAN,
  '<=': SearchOperator.LESS_THAN_OR_EQUAL,
  '!=': SearchOperator.NOT_EQUAL,
};

export class SorareSearchClient {
  // we assume that all facet filters & all numeric filters are coming from disjunctive facets
  protected toRefinements(
    facetFilters: readonly string[][],
    numericFilters: readonly string[] | undefined
  ): SearchRefinementInput[] {
    // Flatten all values and group by field
    const fieldMap = new Map<string, { stringValue: string }[]>();
    facetFilters?.flat().forEach(value => {
      const separatorIndex = value.indexOf(':');
      if (separatorIndex === -1) return;
      const field = value.slice(0, separatorIndex);
      const v = value.slice(separatorIndex + 1);
      if (!field) return;
      if (!fieldMap.has(field)) fieldMap.set(field, []);
      fieldMap.get(field)!.push({ stringValue: v });
    });

    const res: SearchFilterInput[] = Array.from(fieldMap.entries()).map(
      ([field, values]) => ({
        field,
        operator: SearchOperator.EQUAL,
        values,
      })
    );

    if (numericFilters) {
      numericFilters.forEach(numericFilter => {
        const match = numericFilter.match(
          /^([._\-\w]+)(!=|=|<|>|>=|<=)([-0-9.]+)$/
        );
        if (match) {
          const [, field, operator, value] = match;
          res.push({
            field,
            operator: OPERATORS[operator]!,
            values: [
              {
                integerValue: parseInt(value, 10),
              },
            ],
          });
        }
      });
    }
    return res;
  }

  protected toFacets(
    facets: readonly string[] | undefined
  ): SearchFacetDefinitionInput[] {
    return (
      facets?.map(facet => ({
        field: facet,
        limit: 30,
        type: SearchFaceting.DISJUNCTIVE, // consider all facets disjunctive
      })) || []
    );
  }
}

export const useIsSorareSearchClient = () => {
  const { client } = useInstantSearchContext();
  return client instanceof SorareSearchClient;
};
